import { useRef } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
} from "framer-motion";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";
import { organizationLd, websiteLd, professionalServiceLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const MARCEL_PHOTO =
    "https://customer-assets-0z36b82j.emergentagent.net/job_marcel-coaching/artifacts/i4x1ckrv_marcel-strand.jpg";
const BODYGRAPH_MEDALLION =
    "https://customer-assets-0z36b82j.emergentagent.net/job_marcel-coaching/artifacts/10tg8dwj_chart-medaillon.png";

// Terugval-teksten: worden alleen gebruikt als de content-API faalt of te
// langzaam is. De database is de bron van waarheid.
const DEFAULTS = {
    "home.seo-title": "Human Design Studio | Ontdek je eigen blauwdruk",
    "home.seo-description":
        "Human Design coaching door Marcel van der Veen. Begrijp hoe jij van nature keuzes maakt en boek een persoonlijke sessie.",
    "home.hero.eyebrow": "Human Design coaching · Marcel van der Veen",
    "home.hero.title-1": "Lees je",
    "home.hero.title-2": "eigen",
    "home.hero.title-3": "blauwdruk.",
    "home.hero.subtitle":
        "Human Design is een spiegel, geen sticker. Marcel begeleidt je in het lezen van je eigen kaart, zodat je keuzes maakt die kloppen met wie je werkelijk bent.",
    "home.hero.cta-primary": "Boek een sessie",
    "home.hero.cta-secondary": "Wat is Human Design",
    "home.hero.scroll-hint": "Scroll, vertraag",
    "home.hero.bodygraph-foto": {
        src: BODYGRAPH_MEDALLION,
        alt: "Bodygraph van Marcel van der Veen als rond medaillon op zandkleurige achtergrond",
    },
    "home.marquee.items": [
        "Type",
        "Autoriteit",
        "Profiel",
        "Centra",
        "Kanalen",
        "Poorten",
        "Strategie",
    ],
    "home.manifesto.eyebrow": "Ons kompas",
    "home.manifesto.heading-a": "Vier ankers waarop",
    "home.manifesto.heading-accent": "deze studio",
    "home.manifesto.heading-b": "rust.",
    "home.manifesto.1.title": "Rustig",
    "home.manifesto.1.body":
        "Geen shortcuts, geen hype. We werken met wat er is, en met wat er wil landen. Ruimte, tijd, en aandacht als fundament.",
    "home.manifesto.2.title": "Aards",
    "home.manifesto.2.body":
        "Je lichaam weet meer dan je hoofd. Human Design geeft taal aan dat weten, zonder het te vereenvoudigen tot een formule.",
    "home.manifesto.3.title": "Eigenzinnig",
    "home.manifesto.3.body":
        "Geen kant-en-klare adviezen. Wel een spiegel voor keuzes die écht bij jou passen, ook als die tegendraads zijn.",
    "home.manifesto.4.title": "Menselijk",
    "home.manifesto.4.body":
        "Marcel begeleidt één-op-één, in de taal die jij spreekt. Zonder jargon, zonder oordeel. Alleen wat werkt.",
    "home.methodes.eyebrow": "Methodes",
    "home.methodes.heading": "Waarmee ik werk",
    "home.methodes.1.naam": "Human Design",
    "home.methodes.1.zin": "je blauwdruk, hoe je van nature werkt.",
    "home.methodes.2.naam": "NLP",
    "home.methodes.2.zin": "je denken en je gewoontes.",
    "home.methodes.3.naam": "Familieopstellingen",
    "home.methodes.3.zin": "de onderstroom zichtbaar maken.",
    "home.methodes.4.naam": "Lichaamswerk",
    "home.methodes.4.zin": "van denken naar voelen.",
    "home.methodes.cta": "Lees meer over mijn methodes",
    "home.decipher.eyebrow": "Over Human Design",
    "home.decipher.heading-a": "Ontcijfer je",
    "home.decipher.heading-accent": "eigen blauwdruk",
    "home.decipher.body":
        "Door je unieke energetische blauwdruk te ontcijferen, onthult Human Design je sterke punten, uitdagingen en potentieel, waardoor je een leven vol zingeving en voldoening kunt leiden. Het gaat niet om geloof. Het is een experiment, een reis naar de werking van je ware zelf.",
    "home.decipher.cta": "Lees meer",
    "home.portrait.foto": { src: MARCEL_PHOTO, alt: "Marcel van der Veen" },
    "home.portrait.eyebrow": "Over Marcel",
    "home.portrait.heading-a": "Een gids die",
    "home.portrait.heading-accent": "stil",
    "home.portrait.heading-b": "durft te blijven.",
    "home.portrait.body":
        "Marcel van der Veen werkt vanuit een rustige, aardse benadering. Geen dogma's, wél scherpe observatie. Hij vertaalt Human Design naar keuzes die je vandaag kunt maken.",
    "home.portrait.cta": "Ontmoet Marcel",
};

const METHODE_ACCENTS = ["clay", "green", "clay", "green"];

export default function Home() {
    const c = usePageContent("home", DEFAULTS);
    useSeo({
        title: c ? c.t("home.seo-title") : DEFAULTS["home.seo-title"],
        description: c
            ? c.t("home.seo-description")
            : DEFAULTS["home.seo-description"],
        path: "/",
        jsonLd: [organizationLd(), websiteLd(), professionalServiceLd()],
    });

    if (!c) return <ContentLoading />;
    return <HomeContent c={c} />;
}

function HomeContent({ c }) {
    const heroRef = useRef(null);
    const reduced = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });

    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
    const bgOpacity = useTransform(scrollYProgress, [0, 0.8], [0.55, 0.15]);
    const heroContentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

    const lineEase = [0.16, 1, 0.3, 1];

    const heroLines = [
        c.t("home.hero.title-1"),
        c.t("home.hero.title-2"),
        c.t("home.hero.title-3"),
    ];
    const medallion = c.img("home.hero.bodygraph-foto");
    const portrait = c.img("home.portrait.foto");
    // Lopende band: woorden uit de database, scheidingstekens zijn opmaak
    const marqueeItems = c.list("home.marquee.items").flatMap((w) => [w, "·"]);
    const manifesto = [1, 2, 3, 4].map((n) => ({
        n: `0${n}`,
        title: c.t(`home.manifesto.${n}.title`),
        body: c.t(`home.manifesto.${n}.body`),
    }));
    const methodes = [1, 2, 3, 4].map((n) => ({
        naam: c.t(`home.methodes.${n}.naam`),
        zin: c.t(`home.methodes.${n}.zin`),
        accent: METHODE_ACCENTS[n - 1],
    }));

    return (
        <>
            {/* ============== HERO ============== */}
            <section
                ref={heroRef}
                data-testid="home-hero"
                className="relative min-h-[100svh] w-full overflow-hidden bg-[var(--hds-sand)]"
            >
                {/* Parallax sand background */}
                <motion.div
                    aria-hidden
                    style={
                        reduced
                            ? { opacity: 0.4 }
                            : { y: bgY, scale: bgScale, opacity: bgOpacity }
                    }
                    className="absolute inset-0 -z-10"
                >
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(120% 120% at 72% 18%, #EFE7D4 0%, #F6F1E7 46%, #EADFC8 100%)",
                        }}
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(60% 60% at 55% 45%, rgba(246,241,231,0.35) 0%, rgba(246,241,231,0.9) 65%, rgba(246,241,231,1) 100%)",
                        }}
                    />
                </motion.div>

                {/* Terracotta accent orb (3D-ish moment) met bodygraph-medaillon */}
                <motion.div
                    aria-hidden={!medallion.src}
                    data-testid="home-hero-orb"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.6, ease: lineEase, delay: 0.2 }}
                    className="pointer-events-none absolute right-[-14vw] top-[18vh] md:right-[-4vw] md:top-[14vh] lg:right-[3vw] lg:top-[12vh] w-[62vw] h-[62vw] md:w-[42vw] md:h-[42vw] lg:w-[38vw] lg:h-[38vw] rounded-full overflow-hidden"
                    style={{
                        background: medallion.src
                            ? "radial-gradient(circle at 35% 30%, rgba(232,167,118,0.35) 0%, rgba(192,112,63,0.20) 55%, rgba(109,58,28,0.10) 100%)"
                            : "radial-gradient(circle at 35% 30%, #E8A776 0%, #C0703F 45%, #A65C30 75%, #6d3a1c 100%)",
                        boxShadow:
                            "inset -30px -40px 100px rgba(60,25,10,0.55), 0 40px 120px rgba(192,112,63,0.25)",
                    }}
                >
                    {medallion.src && (
                        <img
                            src={medallion.src}
                            alt={medallion.alt}
                            className="absolute inset-[6%] w-[88%] h-[88%] object-contain rounded-full"
                            data-testid="home-hero-bodygraph"
                        />
                    )}
                </motion.div>
                <motion.div
                    aria-hidden
                    animate={
                        reduced
                            ? {}
                            : { y: [0, -14, 0], rotate: [0, 6, 0] }
                    }
                    transition={{
                        duration: 14,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    className="pointer-events-none absolute left-[6vw] bottom-[8vh] w-[110px] h-[110px] md:w-[160px] md:h-[160px] rounded-full border border-[var(--hds-green-soft)]/40"
                />

                <motion.div
                    style={reduced ? {} : { y: heroContentY }}
                    className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10 pt-[26vh] md:pt-[22vh] pb-24"
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="flex items-center gap-4 mb-10"
                    >
                        <span className="inline-block w-10 h-px bg-[var(--hds-clay)]" />
                        <span className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-ink)]/60">
                            {c.t("home.hero.eyebrow")}
                        </span>
                    </motion.div>

                    <h1
                        data-testid="home-hero-title"
                        className="font-[Fraunces] text-[16vw] md:text-[11vw] lg:text-[92px] leading-[0.95] tracking-[-0.03em] text-[var(--hds-ink)] max-w-[15ch]"
                        style={{ fontWeight: 300 }}
                    >
                        {heroLines.map((line, i) => (
                            <span key={i} className="hds-mask-line">
                                <motion.span
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={{
                                        duration: 1.1,
                                        ease: lineEase,
                                        delay: 0.35 + i * 0.11,
                                    }}
                                    className="block"
                                >
                                    {i === 2 ? (
                                        <em
                                            className="italic text-[var(--hds-clay)]"
                                            style={{ fontWeight: 300 }}
                                        >
                                            {line}
                                        </em>
                                    ) : (
                                        line
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <motion.p
                            data-testid="home-hero-subtitle"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 1,
                                ease: lineEase,
                                delay: 0.9,
                            }}
                            className="md:col-span-6 max-w-[46ch] text-[16.5px] md:text-[19px] leading-[1.55] text-[var(--hds-ink)]/78"
                        >
                            {c.t("home.hero.subtitle")}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 1,
                                ease: lineEase,
                                delay: 1.05,
                            }}
                            className="md:col-span-6 flex flex-col sm:flex-row md:justify-end gap-4"
                        >
                            <Button
                                to="/boeken"
                                data-testid="hero-cta-primary"
                                variant="primary"
                                icon="arrow"
                            >
                                {c.t("home.hero.cta-primary")}
                            </Button>
                            <Button
                                to="/wat-is-human-design"
                                data-testid="hero-cta-secondary"
                                variant="secondary"
                            >
                                {c.t("home.hero.cta-secondary")}
                            </Button>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.4 }}
                        className="absolute left-5 md:left-10 bottom-8 flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-[var(--hds-ink)]/50"
                    >
                        <motion.span
                            animate={reduced ? {} : { y: [0, 6, 0] }}
                            transition={{
                                duration: 2.4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            className="inline-block w-px h-8 bg-[var(--hds-ink)]/40"
                        />
                        {c.t("home.hero.scroll-hint")}
                    </motion.div>
                </motion.div>
            </section>

            {/* ============== SLOW EDITORIAL MARQUEE ============== */}
            <section
                data-testid="home-marquee"
                aria-hidden
                className="border-y border-[var(--hds-line)] bg-[var(--hds-card)]/60 overflow-hidden py-6 md:py-10"
            >
                <div className="hds-marquee-track whitespace-nowrap">
                    {[0, 1].map((k) => (
                        <div key={k} className="flex items-center pr-16">
                            {marqueeItems.map((item, idx) => (
                                <span
                                    key={`${k}-${idx}`}
                                    className={`inline-block px-8 font-[Fraunces] text-[10vw] md:text-[8vw] leading-none tracking-[-0.02em] ${
                                        item === "·"
                                            ? "text-[var(--hds-clay)]"
                                            : idx % 3 === 0
                                              ? "italic text-[var(--hds-green)]"
                                              : "text-[var(--hds-ink)]"
                                    }`}
                                    style={{ fontWeight: 300 }}
                                >
                                    {item}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </section>

            {/* ============== MANIFESTO ============== */}
            <section
                data-testid="home-manifesto"
                className="relative mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-40"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, ease: lineEase }}
                        className="md:col-span-4 text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]"
                    >
                        {c.t("home.manifesto.eyebrow")}
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{
                            duration: 1,
                            ease: lineEase,
                            delay: 0.1,
                        }}
                        className="md:col-span-8 font-[Fraunces] text-[9vw] md:text-[5.4vw] lg:text-[60px] leading-[1.05] tracking-[-0.02em] text-[var(--hds-ink)]"
                        style={{ fontWeight: 300 }}
                    >
                        {c.t("home.manifesto.heading-a")}{" "}
                        <em
                            className="italic text-[var(--hds-green)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("home.manifesto.heading-accent")}
                        </em>{" "}
                        {c.t("home.manifesto.heading-b")}
                    </motion.h2>
                </div>

                <div className="space-y-0">
                    {manifesto.map((card, i) => (
                        <motion.article
                            key={card.n}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{
                                duration: 0.9,
                                ease: lineEase,
                                delay: i * 0.05,
                            }}
                            className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-t border-[var(--hds-line)] ${
                                i === manifesto.length - 1
                                    ? "border-b border-[var(--hds-line)]"
                                    : ""
                            }`}
                        >
                            <div className="md:col-span-2">
                                <span
                                    className="font-[Fraunces] text-[46px] md:text-[64px] leading-none text-[var(--hds-clay)]"
                                    style={{
                                        fontWeight: 300,
                                        fontStyle: "italic",
                                    }}
                                >
                                    {card.n}
                                </span>
                            </div>
                            <div className="md:col-span-4">
                                <h3
                                    className="font-[Fraunces] text-[36px] md:text-[46px] leading-[1.02] tracking-[-0.01em]"
                                    style={{ fontWeight: 300 }}
                                >
                                    {card.title}
                                </h3>
                            </div>
                            <p className="md:col-span-6 md:pt-3 text-[16px] md:text-[17.5px] leading-[1.6] text-[var(--hds-ink)]/78 max-w-[50ch]">
                                {card.body}
                            </p>
                        </motion.article>
                    ))}
                </div>
            </section>

            {/* ============== WAARMEE IK WERK ============== */}
            <section
                data-testid="home-methodes"
                className="relative bg-[var(--hds-card)]/50 border-y border-[var(--hds-line)]"
            >
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-32">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-14 md:mb-20">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.8, ease: lineEase }}
                            className="md:col-span-4 text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]"
                        >
                            {c.t("home.methodes.eyebrow")}
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.9, ease: lineEase, delay: 0.1 }}
                            className="md:col-span-8 font-[Fraunces] text-[9vw] md:text-[5.4vw] lg:text-[60px] leading-[1.05] tracking-[-0.02em] text-[var(--hds-ink)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("home.methodes.heading")}
                        </motion.h2>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-16 gap-y-0"
                        data-testid="home-methodes-list"
                    >
                        {methodes.map((m, i) => (
                            <motion.div
                                key={m.naam}
                                data-testid={`home-methode-${i}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{
                                    duration: 0.8,
                                    ease: lineEase,
                                    delay: (i % 2) * 0.08,
                                }}
                                className="group flex items-baseline gap-4 py-6 md:py-7 border-b border-[var(--hds-line)]"
                            >
                                <span
                                    aria-hidden
                                    className={`mt-2 shrink-0 w-2 h-2 rounded-full ${
                                        m.accent === "green"
                                            ? "bg-[var(--hds-green)]"
                                            : "bg-[var(--hds-clay)]"
                                    }`}
                                />
                                <p className="text-[17px] md:text-[19px] leading-[1.5] text-[var(--hds-ink)]">
                                    <span
                                        className="font-[Fraunces]"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {m.naam}:
                                    </span>{" "}
                                    <span className="text-[var(--hds-ink)]/78">
                                        {m.zin}
                                    </span>
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-12 md:mt-16 flex flex-wrap gap-4">
                        <Button
                            to="/methodes"
                            data-testid="home-methodes-cta"
                            variant="primary"
                            icon="arrow"
                        >
                            {c.t("home.methodes.cta")}
                        </Button>
                    </div>
                </div>
            </section>

            {/* ============== ONTCIJFER JE BLAUWDRUK ============== */}
            <section
                data-testid="home-decipher"
                className="relative mx-auto max-w-[1440px] px-5 md:px-10 py-24 md:py-32"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.8, ease: lineEase }}
                        className="md:col-span-4"
                    >
                        <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6">
                            {c.t("home.decipher.eyebrow")}
                        </p>
                        <div
                            aria-hidden
                            className="w-16 h-px bg-[var(--hds-clay)]/50"
                        />
                    </motion.div>
                    <div className="md:col-span-8 max-w-[62ch]">
                        <motion.h2
                            initial={{ opacity: 0, y: 26 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.95, ease: lineEase, delay: 0.05 }}
                            className="font-[Fraunces] text-[9vw] md:text-[5.4vw] lg:text-[60px] leading-[1.05] tracking-[-0.02em] text-[var(--hds-ink)]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("home.decipher.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {c.t("home.decipher.heading-accent")}
                            </em>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{ duration: 0.9, ease: lineEase, delay: 0.15 }}
                            className="mt-8 md:mt-10 text-[17px] md:text-[19px] leading-[1.65] text-[var(--hds-ink)]/82"
                        >
                            {c.t("home.decipher.body")}
                        </motion.p>
                        <div className="mt-10 md:mt-12">
                            <Button
                                to="/wat-is-human-design"
                                data-testid="home-decipher-cta"
                                variant="secondary"
                                icon="arrow"
                            >
                                {c.t("home.decipher.cta")}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============== SPOTLIGHT PORTRAIT ============== */}
            <section
                data-testid="home-portrait"
                className="relative mx-auto max-w-[1440px] px-5 md:px-10 pb-24 md:pb-40"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 60 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1, ease: lineEase }}
                        className="md:col-span-7 relative"
                    >
                        <div
                            className="relative aspect-[4/5] w-full overflow-hidden rounded-[2px]"
                            style={{
                                clipPath:
                                    "polygon(0 0, 100% 4%, 100% 100%, 0 96%)",
                            }}
                        >
                            <img
                                src={portrait.src}
                                alt={portrait.alt}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div
                                aria-hidden
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "radial-gradient(70% 60% at 50% 60%, rgba(0,0,0,0) 0%, rgba(192,112,63,0.06) 80%, rgba(192,112,63,0.10) 100%)",
                                }}
                            />
                        </div>
                    </motion.div>
                    <div className="md:col-span-5 md:pb-6">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.15 }}
                            className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6"
                        >
                            {c.t("home.portrait.eyebrow")}
                        </motion.p>
                        <motion.h3
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.2 }}
                            className="font-[Fraunces] text-[38px] md:text-[52px] leading-[1.02] tracking-[-0.02em]"
                            style={{ fontWeight: 300 }}
                        >
                            {c.t("home.portrait.heading-a")}{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                {c.t("home.portrait.heading-accent")}
                            </em>{" "}
                            {c.t("home.portrait.heading-b")}
                        </motion.h3>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.3 }}
                            className="mt-6 text-[16px] leading-[1.65] text-[var(--hds-ink)]/78 max-w-[42ch]"
                        >
                            {c.t("home.portrait.body")}
                        </motion.p>
                        <Link
                            to="/over-marcel"
                            data-testid="home-portrait-cta"
                            className="inline-flex items-center gap-3 mt-8 text-[var(--hds-ink)] group"
                        >
                            <span className="inline-block w-10 h-px bg-[var(--hds-clay)] transition-all duration-500 group-hover:w-16" />
                            <span className="text-[13.5px] tracking-wide">
                                {c.t("home.portrait.cta")}
                            </span>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
